import { useEffect, useState } from "react";
import { HTTPError } from "ky";
import { toast } from "@/lib/toast";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useActAs } from "@/hooks/useActAs";
import type { AdminUser } from "@/hooks/useAdminUsers";

const TTL_OPTIONS: { hours: 1 | 4 | 12 | 24; label: string }[] = [
  { hours: 1, label: "1 hour" },
  { hours: 4, label: "4 hours" },
  { hours: 12, label: "12 hours (default)" },
  { hours: 24, label: "24 hours" },
];

interface Props {
  user: AdminUser | null;
  open: boolean;
  onClose: () => void;
}

export function ActAsDialog({ user, open, onClose }: Props) {
  const actAs = useActAs();
  const [ttlHours, setTtlHours] = useState<1 | 4 | 12 | 24>(12);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTtlHours(12);
      setReason("");
      setSubmitting(false);
    }
  }, [open, user?.id]);

  async function handleStart() {
    if (!user) return;
    setSubmitting(true);
    try {
      await actAs.start(
        { id: user.id, email: user.email },
        { ttlHours, reason: reason.trim() || undefined },
      );
      onClose();
    } catch (err) {
      // Surface the real reason instead of silently leaving the button
      // stuck in 'Starting…'. Pulls FastAPI's `detail` from the HTTPError
      // response body when available.
      let detail: string | undefined;
      if (err instanceof HTTPError) {
        try {
          const body = await err.response.clone().json();
          if (body && typeof body.detail === "string") detail = body.detail;
        } catch {
          // Non-JSON body — fall through to message.
        }
      }
      const message =
        detail ?? (err instanceof Error ? err.message : "Failed to start act-as");
      toast.error(`Act-as failed: ${message}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Act as user</DialogTitle>
          <DialogDescription>
            {user
              ? `Impersonate ${user.email}. The session is audit-logged and expires automatically.`
              : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Session length</legend>
            {TTL_OPTIONS.map((opt) => {
              const id = `act-as-ttl-${opt.hours}`;
              return (
                <label
                  key={opt.hours}
                  htmlFor={id}
                  className={
                    "flex cursor-pointer items-center gap-3 rounded-md border p-2.5 text-sm transition-colors " +
                    (ttlHours === opt.hours
                      ? "border-primary bg-primary/5"
                      : "hover:bg-muted/50")
                  }
                >
                  <input
                    id={id}
                    type="radio"
                    name="act-as-ttl"
                    value={opt.hours}
                    checked={ttlHours === opt.hours}
                    onChange={() => setTtlHours(opt.hours)}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </fieldset>

          <div className="space-y-1.5">
            <label htmlFor="act-as-reason" className="text-sm font-medium">
              Reason <span className="text-muted-foreground">(optional, audited)</span>
            </label>
            <Input
              id="act-as-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. debugging chat issue from #1234"
              disabled={submitting}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleStart} disabled={!user || submitting}>
            {submitting ? "Starting…" : "Start session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ActAsDialog;
